import { useCallback } from 'react';
import { useNavigate, useLocation, generatePath } from 'react-router-dom';
import { FlowStep, FlowHistoryState } from './types';
import { useCurrentFlow } from './useCurrentFlow';

const isBrowser = typeof window !== 'undefined';

/**
 * React Router stamps a monotonic `idx` on `window.history.state` for every entry
 * — the true count of in-session entries below the current one. We read it to
 * decide whether a history POP is safe (i.e. THIS session actually pushed the
 * entries we'd pop), rather than trusting per-entry flow metadata which is absent
 * on cold / native-webview / deep-link entries. Returns null when unavailable
 * (SSR, MemoryRouter in tests), which callers treat as "can't pop".
 */
const getHistoryIdx = (): number | null => {
    if (!isBrowser) return null;
    const idx = (window.history.state as { idx?: number } | null)?.idx;
    return typeof idx === 'number' ? idx : null;
};

/**
 * Durable in-flow "context" params: the job / draft / apply identifiers and the
 * locale-or-referrer context a destination page needs to render itself.
 *
 * This list is consulted ONLY by the deep-link fallback in `replaceToReturn`.
 * On a normal back/exit we `navigate(-n)` and the browser restores the prior
 * entry's URL — search included — for free; this allowlist never runs. It only
 * matters when there is no entry to pop (cold start / native webview / reload):
 * then the prior URL can't be restored, so we rebuild the return URL from the
 * CURRENT (in-detour) search — and the only params that legitimately belong on
 * the destination are these context params.
 *
 * Crucially this is an **allowlist (default-deny)**: anything not listed — e.g.
 * a detour's own throwaway params like the mid-setup feedback `currentPage` /
 * `setupLogUid` — is dropped, so detour-scoped params can never leak onto a
 * static fallback route (e.g. `/auto-apply-setup`), and any param added in the
 * future is safe-by-default until it is explicitly registered here. Normal
 * forward moves (`pushEntry`) are unaffected and still merge the full search.
 *
 * NOTE: when adding a new param that must survive a deep-link back/exit, add it
 * here. Forgetting yields a visible "param missing after deep-link back" bug
 * (easy to catch), which we prefer over silently leaking detour params.
 */
const DURABLE_SEARCH_PARAMS = [
    'indeedApplyableJobId', // job context (light-apply, auto-apply-setup)
    'draftIds', // selected drafts (auto-apply flow)
    'failedDraftIds', // failed drafts (auto-apply review/confirmation)
    'previewFailedDraftIds', // preview-failed drafts (auto-apply review/confirmation)
    'jobKey', // job context (report-issue)
    'encryptedApplyId', // apply context (report-issue)
    'from', // referrer (resume, past-answers)
    'co', // country/locale
    'hl', // host language/locale
    'prforceGroups', // forced proctor buckets (QA/e2e overrides; must persist across the whole flow)
];

/**
 * Keep ONLY {@link DURABLE_SEARCH_PARAMS} from a search string, returning a
 * normalised `?…` (or `''` when nothing remains). Used to rebuild a return URL
 * on the deep-link fallback without dragging detour-scoped params along.
 * Uses `getAll`/`append` so multi-valued params survive intact.
 */
const pickDurableParams = (search: string): string => {
    const source = new URLSearchParams(search);
    const kept = new URLSearchParams();
    DURABLE_SEARCH_PARAMS.forEach((key) => {
        source.getAll(key).forEach((value) => kept.append(key, value));
    });
    const result = kept.toString();
    return result ? `?${result}` : '';
};

/**
 * **Flow-aware page navigation utilities**
 *
 * Provides intelligent navigation methods that understand your application flows,
 * automatically preserve URL search parameters, and handle advanced features like
 * skip logic and nested sub-flow detours (e.g. opening an edit page from Review).
 *
 * ## **Navigation Method Guide:**
 *
 * | Method           | Use Case                          | Validation      | Search Params | History effect |
 * |------------------|-----------------------------------|-----------------|---------------|----------------|
 * | `goBack()`       | Previous step (spine or sub-flow) | ✅ Flow-aware   | ✅ Auto       | Pop / replace  |
 * | `goForward()`    | Next step (spine or sub-flow)     | ✅ Flow-aware   | ✅ Auto       | Push           |
 * | `goToStep()`     | Jump within flow                  | ✅ Route exists | ✅ Auto       | Push / replace |
 * | `enterSubFlow()` | Open a detour (edit/modal)        | ✅ Flow-aware   | ✅ Auto       | Push (records origin)|
 * | `exitSubFlow()`  | Leave the current detour          | ✅ Flow-aware   | ✅ Auto       | Pop / replace  |
 * | `navigate()`     | Routes outside the current flow   | ❌ None         | ❌ Manual     | Push           |
 *
 * ## **Quick Decision Guide:**
 *
 * ```typescript
 * // ✅ Flow navigation (recommended)
 * goBack();                           // Previous step
 * goForward();                        // Next step (advances within a sub-flow too)
 * goForward({ params: { category } }); // Next step with a parameterized route
 * goToStep('/light-apply/resume');    // Jump within flow
 *
 * // ✅ Sub-flow detours (e.g. edit page from Review, mid-setup feedback modal)
 * enterSubFlow('/auto-apply-setup/resume/edit');  // Open the detour (origin = current path)
 * exitSubFlow('/auto-apply-setup/review');        // Pop back to origin (arg is cold-start fallback)
 *
 * // ✅ Navigation outside the current flow
 * navigate('/');                      // Homepage
 * navigate('/other-flow/step');       // Different flow
 *
 * // ✅ External websites
 * window.location.href = 'https://indeed.com';
 * ```
 *
 * @returns Object containing navigation methods and flow state
 */
export const usePageFlow = (): {
    /** All steps for current flow */
    allSteps: FlowStep[];
    /**
     * Navigate to the previous step in the current flow.
     *
     * Automatically skips steps marked with `skipOnBack: true` and preserves
     * search parameters. Handles flow validation.
     *
     * @example
     * ```typescript
     * const handleBack = () => {
     *     if (canGoBack) {
     *         goBack(); // Flow-aware back navigation
     *     }
     * };
     * ```
     */
    goBack: () => Promise<void>;
    /**
     * Navigate to the next step in the current flow.
     *
     * Uses dynamic skip logic (Light Apply) or static navigation (other flows).
     * Automatically preserves search parameters and handles flow completion.
     * Works on the main spine and within sub-flows alike: when inside a sub-flow
     * detour it advances to the next sub-flow step (pushing an entry so the whole
     * detour pops cleanly on exit), and does nothing at the last sub-flow step.
     *
     * When the next step's route carries path params (e.g. the mid-setup-feedback
     * details step `/auto-apply-setup/mid-setup-feedback/:category`), supply the
     * runtime values via `options.params`; they are substituted with React
     * Router's `generatePath`.
     *
     * @param options - Navigation options
     * @param options.replace - Replace current history entry instead of pushing new one
     * @param options.params - Path param values to fill the resolved route pattern
     *
     * @example
     * ```typescript
     * // Standard forward navigation
     * goForward();
     *
     * // Replace history entry (useful for index pages)
     * goForward({ replace: true });
     *
     * // Sub-flow step whose route depends on user input (category → details):
     * goForward({ params: { category } });
     * ```
     */
    goForward: (options?: { replace?: boolean; params?: Record<string, string> }) => Promise<void>;
    /**
     * Navigate to a specific step within the current flow.
     *
     * ✅ **Use goToStep() for:**
     * - Jumping to steps within the same flow (e.g., stepper navigation)
     * - Flow-internal navigation with validation
     *
     * ❌ **Don't use goToStep() for:**
     * - Homepage navigation ('/' - not part of any flow)
     * - Navigation to different flows
     * - External routes or websites
     *
     * **Features:**
     * - ✅ Validates route exists in current flow
     * - ✅ Automatically preserves URL search parameters
     * - ✅ Provides helpful error messages for invalid routes
     *
     * @param step - Route path that must exist in current flow steps
     * @param options - Navigation options
     * @param options.replace - Replace current history entry instead of pushing new one
     *
     * @example
     * ```typescript
     * // ✅ Valid - within Light Apply flow
     * goToStep('/light-apply/contact-info/2');
     * goToStep('/light-apply/resume');
     *
     * // ✅ With options (useful for fallbacks)
     * goToStep('/light-apply/contact-info/1', { replace: true });
     *
     * // ❌ Invalid - use navigate() instead
     * goToStep('/'); // Homepage - not in any flow
     * goToStep('/smart-apply/resume'); // Different flow
     *
     * // ✅ Correct alternatives
     * navigate('/'); // For homepage
     * navigate('/smart-apply/resume'); // For different flows
     * ```
     */
    goToStep: (step: string, options?: { replace?: boolean }) => Promise<void>;
    /**
     * Enter a sub-flow (e.g. open an edit page from the Review page).
     *
     * Pushes a history entry for `targetRoute` and records the current path as
     * the dynamic origin so the sub-flow can be exited cleanly. Once inside the
     * sub-flow, `goForward()` / `goBack()` move between its steps and exit back to
     * the origin automatically. A cold-start fallback can be supplied at exit time
     * via `exitSubFlow(fallbackRoute)`.
     *
     * @example
     * ```typescript
     * // From the Review page, open the resume editor (origin = current path):
     * enterSubFlow('/auto-apply-setup/resume/edit');
     * ```
     */
    enterSubFlow: (targetRoute: string) => Promise<void>;
    /**
     * Exit the current sub-flow and return to its origin.
     *
     * Pops the current sub-flow level (`navigate(-(currentSubFlowIndex + 1))`,
     * derived from the route's position in the sub-flow definition) when it was
     * entered in this session, or falls back to a `replace` navigation to
     * `fallbackRoute` when there is no poppable origin (deep link / external
     * round-trip return).
     * Within a declared multi-step sub-flow you normally rely on `goForward()` /
     * `goBack()` to exit automatically rather than calling this directly.
     *
     * @param fallbackRoute - Where to go when no origin entry can be popped.
     */
    exitSubFlow: (fallbackRoute?: string) => Promise<void>;
    /**
     * React Router navigate function for direct navigation.
     *
     * ✅ **Use navigate() for:**
     * - Homepage navigation ('/')
     * - Navigation to different flows
     * - External routes outside current flow
     * - When you need navigate() options (replace, state)
     *
     * ❌ **Don't use navigate() for:**
     * - Navigation within current flow (use goToStep() instead)
     * - External websites (use window.location.href)
     *
     * **Note:** Unlike goToStep(), navigate() does NOT automatically preserve
     * search parameters - append manually if needed.
     *
     * @example
     * ```typescript
     * // ✅ External navigation
     * navigate('/'); // Homepage
     * navigate('/smart-apply/contact-info'); // Different flow
     * navigate('/post-apply'); // Shared page
     *
     * // ✅ With options
     * navigate('/', { replace: true, state: { from: '/light-apply' } });
     *
     * // ✅ Preserve search params manually
     * navigate('/' + location.search);
     * ```
     */
    navigate: ReturnType<typeof useNavigate>;
    /** Whether the user can navigate back in the flow */
    canGoBack: boolean;
    /** Whether the user can navigate forward in the flow */
    canGoForward: boolean;
    /** Whether the current step is the first step in the flow */
    isFirstStep: boolean;
    /** Whether the current step is the last step in the flow */
    isLastStep: boolean;
    /** Current step route name */
    currentStep: string | null;
    /** Previous step route name (null if at first step) */
    previousStep: string | null;
    /** Next step route name (null if at last step) */
    nextStep: string | null;
    /** Loading state from current flow (includes nodeId loading and dynamic skip evaluation query loading) */
    loading: boolean;
} => {
    const navigate = useNavigate();
    const location = useLocation();

    // Get current flow and its navigation utilities. findSubFlowIndex defaults to
    // "no sub-flow" so a flow that declares none (the common case) behaves as pure
    // main-spine navigation even if the lookup is absent.
    const {
        allSteps,
        firstStepRoute,
        findSubFlowIndex = () => -1,
        findSubFlow = () => null,
        getNextStep,
        loading,
    } = useCurrentFlow();

    // Per-entry flow-history metadata, read straight from React Router's
    // location.state (which it persists in window.history.state across browser
    // back/forward and reloads — exactly the per-entry persistence we need; we go
    // through React Router rather than writing window.history.state directly,
    // since it owns that object).
    //
    // - subFlowOrigin: the route that triggered the current sub-flow (return
    //   target). Present only when the detour was entered in-app this session;
    //   absent on cold entry (used as the "no recorded origin" fallback signal).
    const historyState = (location.state ?? {}) as FlowHistoryState;
    const subFlowOrigin = historyState.subFlowOrigin ?? null;

    // Whether the CURRENT route is a sub-flow step, and its 0-based position within
    // that sub-flow — BOTH derived from the flow definition (findSubFlowIndex owns
    // the matchPath lookup so parameterized steps like `/mid-setup-feedback/:category`
    // match), NEVER from history state. A detour route
    // is classified identically whether it was entered in-app (enterSubFlow) or cold
    // (deep link, native webview, reload), so goForward/goBack always resolve against
    // the correct step list. `exitSubFlow` pops `currentSubFlowIndex + 1` entries.
    const currentSubFlowIndex = findSubFlowIndex(location.pathname);
    const inSubFlow = currentSubFlowIndex >= 0;
    // The sub-flow definition that owns the current route (when inside a detour).
    // Used to step back within the detour on a cold Back and to resolve the
    // detour's `defaultReturnRoute` when no in-app origin was recorded.
    const owningSubFlow = findSubFlow(location.pathname);

    // A history POP is only safe when this session actually holds `count` entries
    // below us. On a cold / native / deep-link entry idx is small (often 0), so
    // callers fall back to a replace instead of popping out of the app.
    const canPop = useCallback(
        (count: number): boolean => {
            const idx = getHistoryIdx();
            return isBrowser && idx !== null && idx >= count;
        },
        // getHistoryIdx reads window.history at call time; recompute per render so
        // the closure isn't stale, but it has no React deps of its own.
        [location.key],
    );

    // Forward push: merge the current search with the target's own query (target
    // wins on conflict) so durable in-flow params (e.g. indeedApplyableJobId) and
    // a sub-flow's own params (e.g. currentPage/setupLogUid) both survive, then
    // navigate with the given per-entry state. Used by every forward move (main
    // spine, goToStep, enter/advance sub-flow). Mirrors React Router's
    // `navigate(to, { state, replace })` signature.
    const pushEntry = useCallback(
        async (
            route: string,
            options?: { state?: FlowHistoryState; replace?: boolean },
        ): Promise<void> => {
            const [pathname, targetQuery = ''] = route.split('?');
            // Merge via getAll/append (not Object.fromEntries, which collapses
            // repeated keys) so multi-valued params (e.g. draftIds=a&draftIds=b)
            // survive. Target wins on conflict: any key the target carries fully
            // replaces the current values for that key.
            const current = new URLSearchParams(location.search);
            const target = new URLSearchParams(targetQuery);
            const merged = new URLSearchParams();
            current.forEach((value, key) => {
                if (!target.has(key)) {
                    merged.append(key, value);
                }
            });
            target.forEach((value, key) => {
                merged.append(key, value);
            });
            const search = merged.toString();
            await navigate(search ? `${pathname}?${search}` : pathname, {
                state: options?.state,
                replace: options?.replace ?? false,
            });
        },
        [navigate, location.search],
    );

    // Back replace to a sub-flow's origin (preferred) or the supplied fallback.
    // Used on cold start / deep link / external return where there is NO entry to
    // pop — so, unlike a real history pop, we can't restore the prior URL and must
    // rebuild it from the current search. If the target already carries its own
    // query we trust it as-is; otherwise we append ONLY the durable context params
    // (see DURABLE_SEARCH_PARAMS) so job/draft/locale context still returns while
    // detour-scoped params (e.g. feedback's currentPage/setupLogUid) are dropped
    // and never leak onto a static fallback route.
    const replaceToReturn = useCallback(
        async (fallbackRoute?: string): Promise<void> => {
            // Prefer the in-app recorded origin, then an explicit caller fallback
            // (which may carry its own richer search), then the owning sub-flow's
            // declared `defaultReturnRoute`. If none resolve, there is nowhere to
            // return to and we no-op.
            const origin =
                subFlowOrigin ?? fallbackRoute ?? owningSubFlow?.defaultReturnRoute ?? null;
            if (!origin) return;
            const url = origin.includes('?') ? origin : origin + pickDurableParams(location.search);
            await navigate(url, {
                replace: true,
                state: { from: location.pathname },
            });
        },
        [navigate, subFlowOrigin, owningSubFlow, location.pathname, location.search],
    );

    // Resolve the navigation state for the current location, branching on whether
    // the route is a sub-flow step. goBack/goForward read this rather than recomputing.
    const getCurrentFlowState = (): {
        currentStep: string | null;
        previousStep: string | null;
        nextStep: string | null;
        canGoBack: boolean;
        canGoForward: boolean;
    } => {
        const currentRoute = location.pathname || '/';

        // Inside a sub-flow detour (e.g. editing from Review). Back/Continue are
        // always available: goBack/goForward step within the detour or the page
        // exits it explicitly. For the progress stepper, highlight the main step
        // the detour will return to (the recorded origin, or the flow's first step
        // when entered cold) so the bar stays coherent during an edit.
        if (inSubFlow) {
            return {
                currentStep: subFlowOrigin ?? firstStepRoute,
                previousStep: null,
                nextStep: null,
                canGoBack: true,
                canGoForward: true,
            };
        }

        // Find current step index
        const currentIndex = allSteps.findIndex((step) => step.routes.includes(currentRoute));
        const currentStep = currentIndex >= 0 ? allSteps[currentIndex] : allSteps[0];

        // Logic to determine previous step (skip steps where skipOnBack = true)
        let previousStep = null;
        if (currentIndex > 0) {
            // Look backwards for the first step with skipOnBack = false
            for (let i = currentIndex - 1; i >= 0; i--) {
                if (!allSteps[i].skipOnBack) {
                    previousStep = allSteps[i];
                    break;
                }
            }
        }

        // Logic to determine next step
        let nextStep = null;
        if (currentIndex >= 0 && currentIndex < allSteps.length - 1) {
            nextStep = allSteps[currentIndex + 1];
        }

        return {
            currentStep: currentStep.routes[0],
            previousStep: previousStep ? previousStep.routes[0] : null,
            nextStep: nextStep ? nextStep.routes[0] : null,
            canGoBack: previousStep !== null && currentStep.canGoBack !== false,
            canGoForward: nextStep !== null,
        };
    };

    const currentFlowState = getCurrentFlowState();

    /**
     * Exit the current sub-flow and return to its origin.
     *
     * Pops `currentSubFlowIndex + 1` entries — exactly the entries the detour
     * pushed (the current route's position within its sub-flow definition, +1 for
     * the entry step) — so the origin entry is reused (no duplicate). In a nested
     * detour this lands on the parent detour's step, not the main spine.
     *
     * The pop only happens when this session actually pushed those entries
     * (`canPop`). On a cold / native-webview / deep-link entry there is nothing to
     * pop, so it replaces to the recorded origin (when present) or the supplied
     * `fallbackRoute` instead of popping out of the app.
     */
    const exitSubFlow = useCallback(
        async (fallbackRoute?: string): Promise<void> => {
            const popCount = currentSubFlowIndex + 1;
            if (inSubFlow && canPop(popCount)) {
                await navigate(-popCount);
                return;
            }
            await replaceToReturn(fallbackRoute);
        },
        [navigate, inSubFlow, currentSubFlowIndex, canPop, replaceToReturn],
    );

    // Flow-based back navigation (not browser history). One code path covers both
    // the main spine and a sub-flow detour — only the "is a pop safe?" predicate
    // and the cold-start fallback target differ.
    const goBack = useCallback(async () => {
        if (inSubFlow) {
            // At the detour's first (entry) step, Back leaves the sub-flow entirely:
            // delegate to exitSubFlow, which pops the entry when poppable or replaces
            // to the recorded origin / declared return route (falling back to the
            // flow's first step) on a cold entry.
            if (currentSubFlowIndex === 0) {
                await exitSubFlow(owningSubFlow?.defaultReturnRoute ?? firstStepRoute);
                return;
            }

            // Deeper sub-flow step: step back one within the detour. Prefer a true
            // history pop (navigate(-1)) so browser Back and in-app Back agree and
            // the forward stack is preserved (P1) — but only when THIS session
            // actually pushed the entry below (canPop(1)).
            if (canPop(1)) {
                await navigate(-1);
                return;
            }
            // Cold / native / deep-link entry past the first step: step back ONE step
            // within the detour, preserving its own search params — we are not
            // leaving the sub-flow yet, so a replace-to-origin would skip the earlier
            // detour steps.
            const previousStepRoute = owningSubFlow?.steps[currentSubFlowIndex - 1]?.routes[0];
            if (previousStepRoute && !previousStepRoute.includes(':')) {
                await navigate(`${previousStepRoute}${location.search}`, {
                    replace: true,
                    state: { from: location.pathname, subFlowOrigin },
                });
                return;
            }
            // No resolvable previous detour step: leave the sub-flow.
            await exitSubFlow(owningSubFlow?.defaultReturnRoute ?? firstStepRoute);
            return;
        }

        // Main spine: nothing to go back to at the first step.
        if (!currentFlowState.canGoBack || !currentFlowState.previousStep) {
            console.warn('⚠️ Cannot go back - at first step in flow');
            return;
        }

        // On the spine a pop is only the logical previous step when we arrived here
        // by going forward from it (location.state.from). Otherwise (deep link,
        // external entry, skipOnBack divergence) replace to the previous step —
        // replacing (not pushing) avoids stacking a stale entry and keeps browser
        // Back consistent (e.g. deep link → external referrer). The `from` check
        // already excludes cold entries, so no idx guard is needed here.
        const cameFromPreviousStep = historyState.from === currentFlowState.previousStep;
        if (isBrowser && cameFromPreviousStep) {
            await navigate(-1);
            return;
        }
        await replaceToReturn(currentFlowState.previousStep);
    }, [
        inSubFlow,
        canPop,
        subFlowOrigin,
        firstStepRoute,
        currentSubFlowIndex,
        owningSubFlow,
        currentFlowState,
        navigate,
        replaceToReturn,
        exitSubFlow,
        historyState.from,
        location.pathname,
        location.search,
    ]);

    // Flow-based forward navigation with dynamic step evaluation
    const goForward = useCallback(
        async (options?: { replace?: boolean; params?: Record<string, string> }): Promise<void> => {
            const currentRoute = location.pathname || '/';

            // Resolve the next step from the flow definition. getNextStep classifies
            // by route alone, so it returns the next sub-flow step when the current
            // route is inside a detour, or the next main-spine step otherwise.
            let targetStep: string | null = null;
            try {
                targetStep = await getNextStep(currentRoute);
                console.log('[goForward]: Flow navigation result:', targetStep);
            } catch (error) {
                console.warn('[goForward]: Flow navigation failed:', error);
            }

            // Fill any path params (e.g. `:category`) on the resolved route with
            // the runtime values supplied by the caller, using React Router's
            // generatePath. Routes without params pass through unchanged.
            const params: Record<string, string> = options?.params ?? {};
            const resolveRoute = (route: string): string =>
                route.includes(':') ? generatePath(route, params) : route;

            // Inside a sub-flow (classified from the route): advance to the resolved
            // next step, carrying the recorded origin unchanged. Whether we arrived
            // here in-app or cold makes no difference — the route tells us we're in
            // the detour, so this also fixes direct/native entry where there is no
            // seeded state. The pop count on exit is derived from the
            // route's position in the sub-flow definition, so there's nothing to
            // track here. At the last step getNextStep returns null and we do
            // nothing — the page calls exitSubFlow() to leave the detour.
            if (inSubFlow) {
                if (targetStep) {
                    await pushEntry(resolveRoute(targetStep), {
                        state: {
                            from: location.pathname,
                            subFlowOrigin,
                        },
                    });
                }
                return;
            }

            // Main spine: if outside the flow (e.g. index pages) and nothing was
            // resolved, fall back to the first step.
            const isInFlow = allSteps.some((step) => step.routes.includes(currentRoute));
            if (!targetStep && !isInFlow && allSteps.length > 0) {
                targetStep = allSteps[0].routes[0];
                console.log('[goForward]: Using fallback to first step:', targetStep);
            }

            // Ensure we have a valid target step
            if (!targetStep) {
                console.warn('⚠️ No next step available - at end of flow or no steps defined');
                return;
            }

            // Main spine forward push (search merge handled by pushEntry).
            await pushEntry(resolveRoute(targetStep), {
                state: { from: location.pathname },
                replace: options?.replace ?? false,
            });
        },
        [inSubFlow, subFlowOrigin, getNextStep, allSteps, pushEntry, location.pathname],
    );

    /**
     * Navigate to a specific step within the current flow.
     *
     * This function validates the target route exists in the current flow
     * and automatically preserves search parameters.
     *
     * @param step - Route path that must exist in current flow steps
     * @param options - Navigation options
     */
    const goToStep = useCallback(
        async (step: string, options?: { replace?: boolean }): Promise<void> => {
            // IMPORTANT: goToStep only works for routes that exist in the current flow
            // For external routes (like '/' homepage), use navigate() directly
            // Supports steps with multiple routes (e.g., '/resume' and '/resume/edit' as same step)
            const targetStep = allSteps.find((flowStep) => flowStep.routes.includes(step));
            if (!targetStep) {
                // Build available routes list (flatten all routes from all steps)
                const availableRoutes = allSteps.flatMap((flowStep) => flowStep.routes);
                console.error(
                    `❌ Step "${step}" not found in current flow. ` +
                        `Available routes: [${availableRoutes.join(', ')}]. ` +
                        `Use navigate() for external routes.`,
                );
                return;
            }

            const targetRoute = step.startsWith('/') ? step : `/${step}`;

            // pushEntry preserves/merges search params (indeedApplyableJobId, etc.)
            // — callers must NOT append location.search.
            await pushEntry(targetRoute, {
                state: { from: location.pathname },
                replace: options?.replace ?? false,
            });
        },
        [allSteps, pushEntry, location.pathname],
    );

    /**
     * Enter a sub-flow (e.g. open an edit page from Review).
     *
     * Pushes an entry for `targetRoute` and records the current pathname as this
     * detour's origin (the return target). Once there, the route itself marks it as
     * a sub-flow step, so goForward/goBack/exitSubFlow operate within the detour
     * without any depth bookkeeping.
     */
    const enterSubFlow = useCallback(
        async (targetRoute: string): Promise<void> => {
            await pushEntry(targetRoute, {
                state: {
                    from: location.pathname,
                    subFlowOrigin: location.pathname,
                },
            });
        },
        [pushEntry, location.pathname],
    );

    return {
        // All steps for current flow
        allSteps,
        goBack,
        goForward,
        goToStep,

        // Sub-flow navigation
        enterSubFlow,
        exitSubFlow,

        // Direct navigation
        navigate,

        // Flow state helpers
        canGoBack: currentFlowState.canGoBack,
        canGoForward: currentFlowState.canGoForward,
        isFirstStep: !currentFlowState.canGoBack,
        isLastStep: !currentFlowState.canGoForward,
        currentStep: currentFlowState.currentStep,
        previousStep: currentFlowState.previousStep,
        nextStep: currentFlowState.nextStep,

        // Loading state (includes nodeId loading and dynamic skip evaluation query loading)
        loading: loading || false,
    };
};
